import type { ReportRow } from '../../models/report.types'

import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import { category, finiteNumber } from './chartData'
import { chartFieldLabel } from './chartPresentation'
import { firstField, manyFields, type ResolvedChartFields } from './resolveChartFields'

interface HierarchyPoint {
  id: string
  parent: string
  name: string
  value?: number
}

function pointId(path: readonly string[]): string {
  return JSON.stringify(path)
}

function hierarchyData(
  rows: readonly ReportRow[],
  dimensions: readonly string[],
  metric: string,
): HierarchyPoint[] {
  const points = new Map<string, HierarchyPoint>()
  for (const row of rows) {
    const path: string[] = []
    dimensions.forEach((dimension, index) => {
      const name = category(row[dimension])
      const parent = path.length === 0 ? '' : pointId(path)
      path.push(name)
      const id = pointId(path)
      const existing = points.get(id)
      const leaf = index === dimensions.length - 1
      if (existing) {
        if (leaf) existing.value = (existing.value ?? 0) + finiteNumber(row[metric])
        return
      }
      points.set(id, {
        id,
        parent,
        name,
        ...(leaf ? { value: finiteNumber(row[metric]) } : {}),
      })
    })
  }
  return [...points.values()]
}

export function buildHierarchyChartOptions(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
  type: 'treemap' | 'sunburst',
): CategoricalChartOptions | null {
  const dimensions = manyFields(fields, ['dimensions'])
  const metric = firstField(fields, ['metric'])
  if (dimensions.length === 0 || !metric) return null

  return {
    chart: { type },
    title: { text: '' },
    series: [
      {
        type,
        name: chartFieldLabel(input, metric),
        data: hierarchyData(input.rows, dimensions, metric),
        ...(type === 'treemap' ? { layoutAlgorithm: 'squarified', allowTraversingTree: true } : {}),
      },
    ],
    credits: { enabled: false },
  } as CategoricalChartOptions
}
